import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_DEFAULT_DESCRIPTION,
  SITE_DEFAULT_KEYWORDS,
  SITE_DEFAULT_OG_TYPE,
  SITE_DEFAULT_SHARE_IMAGE,
  SITE_DEFAULT_TITLE,
  SITE_DEFAULT_TWITTER_CARD,
  SITE_DEFAULT_TWITTER_HANDLE,
} from '../config/siteHeadDefaults';
import { PREST0_ORGANIZATION_REF } from '../config/shellStructuredData';
import { appPathname, headWritesNoindex } from '../design/previewPath';

/** Where search engines and link previews file every page: production's own origin, whichever host served it. */
export const CANONICAL_ORIGIN = 'https://prest0.ai';

/**
 * A page's address as search engines and link previews should file it, for a page that names none itself
 * (2026-09-29). It is production's origin plus the app's own path. It never carries:
 *  - the visitor's host (a preview host, the Firebase Hosting domain);
 *  - the query string or fragment (a `?utm_source=` link);
 *  - the new design's tree prefix (/prest0design/about files as /about);
 *  - a trailing slash, except the root's (the shape of every canonical a page names itself).
 */
export function defaultCanonicalUrl(pathname?: string): string {
  const path = appPathname(pathname).replace(/\/+$/, '');
  return CANONICAL_ORIGIN + (path || '/');
}

interface SeoHeadProps {
  /**
   * Primary meta title used for OpenGraph/Twitter/SEO tags.
   * The browser tab title will be normalized automatically.
   */
  title?: string;
  /**
   * Optional explicit tab title. When provided it will still be normalized to include the site suffix.
   */
  tabTitle?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterCreator?: string;
  twitterSite?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  /**
   * A WebPage block for a page that asks for one (every value but 'Organization' writes it). With neither this nor
   * `structuredData`, the page writes no structured data of its own: the shell's Organization and WebSite are on every
   * page, and a second description of the company is a second company (2026-09-30, src/config/shellStructuredData.ts).
   */
  schemaType?: 'Organization' | 'Article' | 'Product' | 'WebPage' | 'FAQPage' | 'Event';
  /** The page's own structured data: one block, or several (written as one list). An empty list writes none. */
  structuredData?: object | readonly object[];
}

const SeoHead: React.FC<SeoHeadProps> = (props) => {
  const {
    title = SITE_DEFAULT_TITLE,
    tabTitle,
    description = SITE_DEFAULT_DESCRIPTION,
    keywords = SITE_DEFAULT_KEYWORDS,
    ogImage = SITE_DEFAULT_SHARE_IMAGE,
    ogUrl,
    ogType = SITE_DEFAULT_OG_TYPE,
    twitterCard = SITE_DEFAULT_TWITTER_CARD,
    twitterCreator = SITE_DEFAULT_TWITTER_HANDLE,
    twitterSite = SITE_DEFAULT_TWITTER_HANDLE,
    twitterTitle,
    twitterDescription,
    twitterImage,
    canonicalUrl,
    noIndex = false,
    schemaType,
    structuredData,
  } = props;
  
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  // The page's own canonical wins, then the address it hands link previews, then its clean production address.
  const fullCanonicalUrl = canonicalUrl || ogUrl || defaultCanonicalUrl();
  // A link preview files the page where search does, unless the page names a different address itself.
  const fullOgUrl = ogUrl || fullCanonicalUrl;
  const ogImageUrl = ogImage && ogImage.startsWith('http') ? ogImage : (siteUrl ? siteUrl + ogImage : ogImage);
  
  // Default structured data for WebPage
  const defaultWebPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": fullCanonicalUrl,
    "publisher": PREST0_ORGANIZATION_REF
  };

  // The page's own structured data; else a WebPage block when it asks for one; else none of its own.
  const ownData = structuredData ?? (schemaType && schemaType !== 'Organization' ? defaultWebPageData : null);
  const structuredBlocks: readonly object[] = ownData === null ? [] : Array.isArray(ownData) ? ownData : [ownData as object];

  const siteName = 'prest0';
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const normalizeTabTitle = (raw?: string) => {
    const fallback = siteName;
    if (!raw) return fallback;

    const trimmed = raw.trim();
    if (!trimmed) return fallback;

    const escapedSiteName = escapeRegExp(siteName);
    // The site's name is stripped only as a whole word: "Prest0n for law firms" begins with the PRODUCT's name, whose
    // first six letters are the site's (Miles, 2026-11-03: /solutions/law's tab read "n for law firms and legal teams").
    const stripSiteSuffix = trimmed.replace(new RegExp(`\\s*[|–—-]\\s*${escapedSiteName}$`, 'i'), '').trim();
    const stripLeadingSite = stripSiteSuffix.replace(new RegExp(`^${escapedSiteName}(?![A-Za-z0-9])\\s*[:\\-|–—]?\\s*`, 'i'), '').trim();

    if (!stripLeadingSite) {
      return siteName;
    }

    if (stripLeadingSite.toLowerCase() === siteName.toLowerCase()) {
      return siteName;
    }

    return `${stripLeadingSite} | ${siteName}`;
  };

  const metaTitle = title || siteName;
  const documentTitle = normalizeTabTitle(tabTitle || title);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{documentTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullOgUrl} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:title" content={twitterTitle || metaTitle} />
      <meta name="twitter:description" content={twitterDescription || description} />
      <meta name="twitter:image" content={twitterImage || ogImageUrl} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* No Index if specified; in the tree the preview's own noindex is already the one (headWritesNoindex) */}
      {headWritesNoindex(noIndex) && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD Structured Data: the page's own, when it has any */}
      {structuredBlocks.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(structuredBlocks.length === 1 ? structuredBlocks[0] : structuredBlocks)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead; 
